import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProducts } from '../hooks/useProducts';
import ProductTable from './ProductTable';
import SearchBar from './SearchBar';
import PlaceholderFeature from './PlaceholderFeature';
import AdminHeader from './AdminHeader';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { token, isAdmin, isAuthenticated } = useAuth();
  const [activeFeature, setActiveFeature] = useState(null);

  // Don't render anything if not authenticated
  if (!token || !isAuthenticated() || !isAdmin()) {
    return null;
  }

  const handleFeatureClick = (feature) => {
    setActiveFeature(feature);
  };

  const handleBackToDashboard = () => {
    setActiveFeature(null);
  };

  // Feature configurations
  const features = {
    inventory: {
      title: 'Product Inventory Management',
      description: 'Manage product inventory, stock levels, and product information',
      icon: '📦',
      component: ProductInventoryManagement
    },
    invoice: {
      title: 'Invoice Management',
      description: 'Create, manage, and track customer invoices',
      icon: '🧾',
      component: PlaceholderFeature
    },
    quotation: {
      title: 'Quotation Management',
      description: 'Generate and manage customer quotations',
      icon: '📋',
      component: PlaceholderFeature
    },
    customers: {
      title: 'Customer Management',
      description: 'Manage customer information and relationships',
      icon: '👥',
      component: PlaceholderFeature
    },
    reports: {
      title: 'Reports & Analytics',
      description: 'View business reports and analytics dashboard',
      icon: '📊',
      component: PlaceholderFeature
    },
    settings: {
      title: 'System Settings',
      description: 'Configure system settings and preferences',
      icon: '⚙️',
      component: PlaceholderFeature
    }
  };

  // If a specific feature is selected, show that feature
  if (activeFeature) {
    const feature = features[activeFeature];
    const FeatureComponent = feature.component;
    
    if (activeFeature === 'inventory') {
      return <FeatureComponent onBack={handleBackToDashboard} />;
    } else {
      return <FeatureComponent 
        title={feature.title}
        description={feature.description}
        icon={feature.icon}
        onBack={handleBackToDashboard}
      />;
    }
  }

  // Main dashboard with cards
  return (
    <div className="admin-dashboard">
      <AdminHeader title="Admin Dashboard" />

      <main className="dashboard-main">
        <div className="dashboard-content">
          <h2>Management Features</h2>
          <div className="feature-cards">
            <div className="feature-card" onClick={() => handleFeatureClick('inventory')}>
              <div className="card-icon">📦</div>
              <h3>Product Inventory Management</h3>
              <p>Manage product inventory, stock levels, and product information</p>
              <div className="card-status active">Available</div>
            </div>

            <div className="feature-card" onClick={() => handleFeatureClick('invoice')}>
              <div className="card-icon">🧾</div>
              <h3>Invoice Management</h3>
              <p>Create, manage, and track customer invoices</p>
              <div className="card-status coming-soon">Coming Soon</div>
            </div>

            <div className="feature-card" onClick={() => handleFeatureClick('quotation')}>
              <div className="card-icon">📋</div>
              <h3>Quotation Management</h3>
              <p>Generate and manage customer quotations</p>
              <div className="card-status coming-soon">Coming Soon</div>
            </div>

            <div className="feature-card" onClick={() => handleFeatureClick('customers')}>
              <div className="card-icon">👥</div>
              <h3>Customer Management</h3>
              <p>Manage customer information and relationships</p>
              <div className="card-status coming-soon">Coming Soon</div>
            </div>

            <div className="feature-card" onClick={() => handleFeatureClick('reports')}>
              <div className="card-icon">📊</div>
              <h3>Reports & Analytics</h3>
              <p>View business reports and analytics dashboard</p>
              <div className="card-status coming-soon">Coming Soon</div>
            </div>

            <div className="feature-card" onClick={() => handleFeatureClick('settings')}>
              <div className="card-icon">⚙️</div>
              <h3>System Settings</h3>
              <p>Configure system settings and preferences</p>
              <div className="card-status coming-soon">Coming Soon</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Product Inventory Management Component
const ProductInventoryManagement = ({ onBack }) => {
  const { token } = useAuth();
  const productsHook = useProducts(token);

  return (
    <div className="admin-dashboard">
      <AdminHeader 
        title="Product Inventory Management" 
        onBack={onBack}
        showBackButton={true}
      />

      <main className="dashboard-main">
        <div className="breadcrumb">
          <span className="breadcrumb-item" onClick={onBack}>Admin Dashboard</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item active">Product Inventory Management</span>
        </div>

        <div className="dashboard-content">
          <SearchBar 
            searchTerm={productsHook.searchTerm}
            setSearchTerm={productsHook.setSearchTerm}
            hasChanges={productsHook.hasChanges}
            saveChanges={productsHook.saveChanges}
            undoChanges={productsHook.undoChanges}
            updating={productsHook.updating}
            tableLocked={productsHook.tableLocked}
            editedData={productsHook.editedData}
          />
          
          {productsHook.loading ? (
            <div className="loading-overlay">
              <div className="spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : (
            <ProductTable 
              products={productsHook.filteredProducts || []}
              updateProduct={productsHook.updateProduct}
              handleSort={productsHook.handleSort}
              getSortIndicator={productsHook.getSortIndicator}
              isLowStock={productsHook.isLowStock}
              formatPrice={productsHook.formatPrice}
              updating={productsHook.updating}
              tableLocked={productsHook.tableLocked}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
